
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Card, Spinner } from "react-bootstrap";
import axios from "axios";
import "./index.css";
import { BHAREE_BASE_URL_LOCAL } from "../../../config/config.url";
import { useNavigate } from "react-router-dom";

const REDEMPTION_POINTS = 1000;

const VOUCHER_OPTIONS = [
  {
    value: "AMAZON",
    label: "Amazon",
    description: "₹1000 Amazon Gift Voucher",
    icon: "A",
  },
  {
    value: "FLIPKART",
    label: "Flipkart",
    description: "₹1000 Flipkart Voucher",
    icon: "F",
  },
  {
    value: "DMART",
    label: "DMart",
    description: "₹1000 DMart Voucher",
    icon: "D",
  },
  {
    value: "REDEEM_POINTS_TO_CASH",
    label: "Redeem Points to Cash",
    description: "₹1000 Cash Payout",
    icon: "₹",
  },
];

const ReferralHistory = () => {
  const [search, setSearch] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [sendingEmailId, setSendingEmailId] = useState(null);
const [emailMessage, setEmailMessage] = useState("");
const [emailError, setEmailError] = useState("");

  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);

  const [selectedVoucher, setSelectedVoucher] = useState("");
  const [voucherRequests, setVoucherRequests] = useState([]);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherMessage, setVoucherMessage] = useState("");
  const [voucherError, setVoucherError] = useState("");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("jwtToken");
const navigate = useNavigate();

const handleReferAgain = (e) => {
  e.preventDefault();
  navigate("/referral");
};

  // ---------------- REFERRAL HELPERS ----------------

  const getReferralName = (item) =>
    item?.refereeName ?? item?.referee_name ?? "-";

  const getReferralMobile = (item) =>
    item?.refereeMobile ?? item?.referee_mobile ?? "-";

  const getReferralDate = (item) =>
    item?.createdAt ?? item?.created_at ?? null;

  const getReferralPoints = (item) => {
    const points = Number(
      item?.pointsAwarded ??
        item?.points_awarded ??
        item?.points ??
        0
    );

    return Number.isFinite(points) ? points : 0;
  };

  // ---------------- VOUCHER HELPERS ----------------

  const getVoucherType = (voucher) =>
    voucher?.voucherType ??
    voucher?.voucher_type ??
    "";

  const getVoucherLabel = (voucherOrType) => {
    const rawType =
      typeof voucherOrType === "string"
        ? voucherOrType
        : getVoucherType(voucherOrType);

    const normalized = String(rawType)
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");

    const option = VOUCHER_OPTIONS.find(
      (item) => item.value === normalized
    );

    if (option) return option.label;

    // Support older API values.
    if (normalized.includes("AMAZON")) return "Amazon";
    if (normalized.includes("FLIPKART")) return "Flipkart";
    if (normalized.includes("DMART")) return "DMart";

    if (
      normalized.includes("CASH") ||
      normalized.includes("POINTS_TO_CASH")
    ) {
      return "Redeem Points to Cash";
    }

    return rawType || "Voucher";
  };

  const getCouponCode = (voucher) =>
    voucher?.couponCode ??
    voucher?.coupon_code ??
    "";

  const getVoucherValue = (voucher) =>
    voucher?.voucherValue ??
    voucher?.voucher_value ??
    voucher?.value ??
    null;

  const getVoucherRequestedAt = (voucher) =>
    voucher?.requestedAt ??
    voucher?.requested_at ??
    voucher?.createdAt ??
    voucher?.created_at ??
    null;

  const getVoucherExpiry = (voucher) =>
    voucher?.validUntil ??
    voucher?.valid_until ??
    voucher?.expiryDate ??
    voucher?.expiry_date ??
    voucher?.expiresAt ??
    voucher?.expires_at ??
    null;

  const getVoucherCustomerId = (voucher) =>
    voucher?.customerId ??
    voucher?.customer_id ??
    voucher?.clientId ??
    voucher?.client_id ??
    null;

  const getVoucherStatus = (voucher) =>
    String(voucher?.status ?? "")
      .trim()
      .toLowerCase();

  const getVoucherReferralIds = (voucher) => {
    const ids =
      voucher?.referralIds ??
      voucher?.referral_ids ??
      [];

    return Array.isArray(ids) ? ids : [];
  };

  // ---------------- GENERAL HELPERS ----------------

  const getErrorMessage = (err, fallback) => {
    if (err?.response?.status === 401) {
      return "Your session has expired. Please log in again.";
    }

    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "-";

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Display fallback only if the API has not returned an expiry.
  // The backend must save the official expiry for admin validation.
  const getDisplayExpiry = (voucher) => {
    const savedExpiry = getVoucherExpiry(voucher);

    if (savedExpiry) return savedExpiry;

    const requestedAt = getVoucherRequestedAt(voucher);

    if (!requestedAt) return null;

    const date = new Date(requestedAt);

    if (Number.isNaN(date.getTime())) return null;

    // Add 3 calendar months while handling month-end dates.
    const originalDay = date.getDate();

    date.setDate(1);
    date.setMonth(date.getMonth() + 3);

    const lastDayOfMonth = new Date(
      date.getFullYear(),
      date.getMonth() + 1,
      0
    ).getDate();

    date.setDate(Math.min(originalDay, lastDayOfMonth));

    return date;
  };

  // ---------------- GET MY REFERRALS ----------------

  const fetchReferrals = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setReferrals([]);
      setError("You are not logged in. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${BHAREE_BASE_URL_LOCAL}/referrals/my`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("REFERRAL HISTORY RESPONSE:", response.data);

      if (response.data?.success) {
        const result = response.data.data;
        setReferrals(Array.isArray(result) ? result : []);
        setError("");
      } else {
        setReferrals([]);
        setError(
          response.data?.message ||
            "Failed to fetch referral history."
        );
      }
    } catch (err) {
      console.error("GET REFERRAL HISTORY ERROR:", err);
      setReferrals([]);
      setError(
        getErrorMessage(err, "Failed to load referral history.")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------- GET MY WALLET ----------------

  const fetchWallet = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setWalletBalance(0);
      setWalletLoading(false);
      return;
    }

    try {
      setWalletLoading(true);

      const response = await axios.get(
        `${BHAREE_BASE_URL_LOCAL}/wallets/my`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("MY WALLET RESPONSE:", response.data);

      if (response.data?.success) {
        setWalletBalance(
          Number(response.data?.data?.balance ?? 0)
        );
      } else {
        setWalletBalance(0);
      }
    } catch (err) {
      console.error("GET WALLET ERROR:", err);
      setWalletBalance(0);
    } finally {
      setWalletLoading(false);
    }
  }, []);

  // ---------------- GET MY VOUCHER REQUESTS ----------------

  const fetchVoucherRequests = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setVoucherRequests([]);
      return;
    }

    try {
      const response = await axios.get(
        `${BHAREE_BASE_URL_LOCAL}/voucher-requests/my`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("MY VOUCHER REQUESTS RESPONSE:", response.data);

      if (response.data?.success) {
        const result = response.data.data;
        setVoucherRequests(
          Array.isArray(result) ? result : []
        );
      } else {
        setVoucherRequests([]);
      }
    } catch (err) {
      console.error("GET VOUCHER REQUESTS ERROR:", err);
      setVoucherRequests([]);
    }
  }, []);

  // ---------------- LOAD DATA ----------------

  useEffect(() => {
    fetchReferrals();
    fetchWallet();
    fetchVoucherRequests();
  }, [fetchReferrals, fetchWallet, fetchVoucherRequests]);

  // ---------------- SEARCH ----------------

  const filteredReferrals = useMemo(() => {
    const searchText = search.toLowerCase().trim();

    if (!searchText) return referrals;

    return referrals.filter((item) =>
      [
        getReferralName(item),
        getReferralMobile(item),
        item?.company,
        item?.category,
        item?.couponCode,
        item?.coupon_code,
        item?.status,
        item?.city,
        item?.district,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchText)
      )
    );
  }, [referrals, search]);

  // This is lifetime points reported on referral rows.
  // Wallet balance is the remaining redeemable points.
  const totalPointsEarned = referrals.reduce(
    (total, item) => total + getReferralPoints(item),
    0
  );

  // ---------------- VOUCHER REQUEST STATE ----------------

  const existingVoucherRequest = voucherRequests.find(
    (request) => getVoucherStatus(request) === "pending"
  );

  const latestVoucherRequest = voucherRequests.length
    ? [...voucherRequests].sort((a, b) => {
        const dateA = new Date(
          getVoucherRequestedAt(a) || 0
        ).getTime();

        const dateB = new Date(
          getVoucherRequestedAt(b) || 0
        ).getTime();

        return dateB - dateA;
      })[0]
    : null;

  // Issued voucher for download. A cash request may not have a coupon code.
  const latestVoucher =
    voucherRequests.find(
      (request) =>
        ["sent", "approved", "completed"].includes(
          getVoucherStatus(request)
        ) &&
        getCouponCode(request)
    ) || null;

  // ---------------- SELECT VOUCHER ----------------

  const handleVoucherSelect = (voucherType) => {
    setVoucherMessage("");
    setVoucherError("");

    if (walletBalance < REDEMPTION_POINTS) {
      setVoucherError(
        `You need at least ${REDEMPTION_POINTS} points to redeem a reward.`
      );
      return;
    }

    if (existingVoucherRequest) {
      setVoucherError(
        "You already have a pending voucher request."
      );
      return;
    }

    setSelectedVoucher(voucherType);
  };

  // ---------------- SUBMIT REDEMPTION ----------------

  const handleSubmitRedemption = async () => {
    setVoucherMessage("");
    setVoucherError("");

    const token = getToken();

    if (!token) {
      setVoucherError("Please log in again.");
      return;
    }

    if (!selectedVoucher) {
      setVoucherError("Please select a redemption option.");
      return;
    }

    if (
      !VOUCHER_OPTIONS.some(
        (option) => option.value === selectedVoucher
      )
    ) {
      setVoucherError("Please select a valid redemption option.");
      return;
    }

    if (walletBalance < REDEMPTION_POINTS) {
      setVoucherError(
        `You need at least ${REDEMPTION_POINTS} points to redeem a reward.`
      );
      return;
    }

    if (existingVoucherRequest) {
      setVoucherError(
        "You already have a pending voucher request."
      );
      return;
    }

    try {
      setVoucherLoading(true);

      const response = await axios.post(
        `${BHAREE_BASE_URL_LOCAL}/voucher-requests`,
        {
          voucherType: selectedVoucher,
          points: REDEMPTION_POINTS,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("CREATE VOUCHER RESPONSE:", response.data);

      if (response.data?.success) {
        setVoucherMessage(
          response.data.message ||
            "Redemption request submitted successfully."
        );

        setSelectedVoucher("");

        await Promise.all([
          fetchVoucherRequests(),
          fetchWallet(),
        ]);
      } else {
        setVoucherError(
          response.data?.message ||
            "Failed to submit redemption request."
        );
      }
    } catch (err) {
      console.error("SUBMIT VOUCHER REQUEST ERROR:", err);
      console.error(
        "VOUCHER REQUEST STATUS:",
        err.response?.status
      );
      console.error(
        "VOUCHER REQUEST RESPONSE:",
        err.response?.data
      );
      console.error(
        "VOUCHER REQUEST URL:",
        err.config?.url
      );
      console.error(
        "VOUCHER REQUEST PAYLOAD:",
        err.config?.data
      );

      setVoucherError(
        getErrorMessage(
          err,
          "Failed to submit redemption request."
        )
      );
    } finally {
      setVoucherLoading(false);
    }
  };

  // ---------------- REFERRAL VOUCHER MATCHING ----------------

  const isReferralIncludedInVoucher = (item) => {
    if (!latestVoucher) return false;

    const includedIds = getVoucherReferralIds(latestVoucher);

    const referralId = item?.id ?? item?._id;

    if (referralId == null) return false;

    return includedIds.some(
      (id) => String(id) === String(referralId)
    );
  };

  // ---------------- DOWNLOAD / PRINT VOUCHER ----------------

  const handleDownloadVoucher = (voucher = latestVoucher) => {
    if (!voucher || !getCouponCode(voucher)) {
      setVoucherError(
        "The voucher code is not available yet."
      );
      return;
    }

    setVoucherError("");

    const escapeHtml = (value) =>
      String(value ?? "").replace(/[&<>"']/g, (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char]
      );

    const rawType = getVoucherType(voucher);
    const voucherLabel = getVoucherLabel(rawType);
    const normalizedType = String(rawType)
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");

    const couponCode = escapeHtml(getCouponCode(voucher));
    const voucherValue = escapeHtml(
      getVoucherValue(voucher) ?? "₹1000"
    );
    const requestedAt = getVoucherRequestedAt(voucher);
    const expiry = getDisplayExpiry(voucher);

    const requestDateText = escapeHtml(
      formatDate(requestedAt)
    );

    const expiryDateText = escapeHtml(
      formatDate(expiry)
    );

    const customerId = escapeHtml(
      getVoucherCustomerId(voucher) ||
        getCouponCode(voucher).replace(/\s+/g, "")
    );

    let brandTitle = voucherLabel;
    let subtitle = "REDEMPTION VOUCHER";

    if (normalizedType.includes("AMAZON")) {
      brandTitle = "Amazon";
      subtitle = "GIFT CARD";
    } else if (normalizedType.includes("FLIPKART")) {
      brandTitle = "Flipkart";
      subtitle = "GIFT VOUCHER";
    } else if (normalizedType.includes("DMART")) {
      brandTitle = "DMart";
      subtitle = "GIFT VOUCHER";
    } else if (
      normalizedType.includes("CASH") ||
      normalizedType.includes("POINTS_TO_CASH")
    ) {
      brandTitle = "Redeem Points to Cash";
      subtitle = "CASH REDEMPTION";
    }

    const safeBrandTitle = escapeHtml(brandTitle);
    const safeSubtitle = escapeHtml(subtitle);
    const safeVoucherLabel = escapeHtml(voucherLabel);

    const voucherWindow = window.open(
      "",
      "_blank",
      "width=800,height=850"
    );

    if (!voucherWindow) {
      setVoucherError(
        "Please allow popups to print your voucher."
      );
      return;
    }

    voucherWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <title>${safeBrandTitle} - Bharee Voucher</title>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 30px 16px;
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #eef2f6;
              color: #111827;
              font-family: Arial, Helvetica, sans-serif;
            }

            .wrapper {
              width: 100%;
              max-width: 480px;
            }

            .card {
              width: 100%;
              overflow: hidden;
              border-radius: 24px;
              background: #fff;
              box-shadow: 0 15px 40px rgba(0, 0, 0, .1);
            }

            .card-top {
              background: #111827;
              color: white;
              padding: 28px;
            }

            .top-header {
              display: flex;
              justify-content: space-between;
              gap: 15px;
              align-items: flex-start;
              margin-bottom: 38px;
            }

            .brand-badge {
              padding: 9px 14px;
              border-radius: 9px;
              background: white;
              color: #111827;
              font-size: 17px;
              font-weight: 800;
            }

            .brand-title {
              text-align: right;
              overflow-wrap: anywhere;
            }

            .brand-title strong {
              display: block;
              font-size: 19px;
            }

            .brand-title small {
              display: block;
              margin-top: 5px;
              color: #d1d5db;
              font-size: 10px;
              letter-spacing: 1.5px;
            }

            .coupon-code {
              margin-bottom: 30px;
              font-size: 22px;
              font-weight: 800;
              letter-spacing: 2px;
              overflow-wrap: anywhere;
            }

            .validity {
              display: flex;
              justify-content: space-between;
              gap: 15px;
              align-items: flex-end;
            }

            .validity small {
              display: block;
              margin-bottom: 6px;
              color: #d1d5db;
              font-size: 10px;
              letter-spacing: 1px;
            }

            .validity strong {
              font-size: 14px;
            }

            .card-bottom {
              padding: 28px;
            }

            .info-row {
              display: flex;
              justify-content: space-between;
              gap: 18px;
              margin-bottom: 25px;
            }

            .info-label {
              margin: 0 0 8px;
              color: #6b7280;
              font-size: 10px;
              font-weight: 800;
              letter-spacing: 1px;
            }

            .info-value {
              margin: 0;
              color: #111827;
              font-size: 17px;
              font-weight: 800;
              overflow-wrap: anywhere;
            }

            .amount {
              text-align: right;
              font-size: 22px;
            }

            .status-badge {
              display: inline-block;
              padding: 8px 12px;
              border-radius: 20px;
              background: #ffebee;
              color: #c62828;
              font-size: 10px;
              font-weight: 800;
              letter-spacing: .5px;
            }

            .date-value {
              font-size: 14px;
            }

            .print-button {
              display: block;
              width: 100%;
              margin-top: 22px;
              padding: 15px;
              border: 0;
              border-radius: 12px;
              background: #111827;
              color: #fff;
              font-size: 14px;
              font-weight: 700;
              cursor: pointer;
            }

            .note {
              margin-top: 12px;
              color: #6b7280;
              font-size: 12px;
              text-align: center;
            }

            @media print {
              @page {
                size: A5 portrait;
                margin: 10mm;
              }

              body {
                display: block;
                padding: 0;
                background: #fff;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }

              .wrapper {
                max-width: 100%;
              }

              .card {
                border: 1px solid #e5e7eb;
                box-shadow: none;
                break-inside: avoid;
              }

              .print-button,
              .note {
                display: none !important;
              }
            }
          </style>
        </head>

        <body>
          <div class="wrapper">
            <main class="card">
              <section class="card-top">
                <div class="top-header">
                  <div class="brand-badge">Bharee</div>

                  <div class="brand-title">
                    <strong>${safeBrandTitle}</strong>
                    <small>${safeSubtitle}</small>
                  </div>
                </div>

                <div class="coupon-code">${couponCode}</div>

                <div class="validity">
                  <div>
                    <small>REQUEST DATE</small>
                    <strong>${requestDateText}</strong>
                  </div>

                  <div style="text-align:right">
                    <small>VALID UNTIL</small>
                    <strong>${expiryDateText}</strong>
                  </div>
                </div>
              </section>

              <section class="card-bottom">
                <div class="info-row">
                  <div>
                    <p class="info-label">SELECTED REWARD</p>
                    <h3 class="info-value">${safeVoucherLabel}</h3>
                  </div>

                  <div>
                    <p class="info-label" style="text-align:right">
                      VALUE
                    </p>
                    <h3 class="info-value amount">${voucherValue}</h3>
                  </div>
                </div>

                <div class="info-row" style="margin-bottom:0">
                  <div>
                    <p class="info-label">CUSTOMER / CLIENT ID</p>
                    <p class="info-value date-value">${customerId}</p>
                  </div>

                  <div style="text-align:right">
                    <span class="status-badge">REDEMPTION</span>
                  </div>
                </div>
              </section>
            </main>

            <button
              class="print-button"
              type="button"
              onclick="window.print()"
            >
              Download / Print Voucher
            </button>

            <p class="note">
              Choose “Save as PDF” in the print window to save your voucher.
            </p>
          </div>
        </body>
      </html>
    `);

    voucherWindow.document.close();
  };
  const handleSendReferralEmail = async (item) => {
  const referralId = item?.id ?? item?._id;

  if (!referralId || sendingEmailId !== null) return;

  setEmailMessage("");
  setEmailError("");

  try {
    setSendingEmailId(referralId);

    const token = getToken();

    if (!token) {
      setEmailError("Please log in again.");
      return;
    }

    const response = await axios.post(
      `${BHAREE_BASE_URL_LOCAL}/referrals/${referralId}/resend-email`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      setEmailMessage(
        `Welcome email sent successfully to ${getReferralName(item)}.`
      );
    } else {
      setEmailError(
        response.data?.message || "Failed to send email."
      );
    }
  } catch (error) {
    console.error("SEND REFERRAL EMAIL ERROR:", error);

    setEmailError(
      error.response?.data?.message ||
        "Failed to send referral email."
    );
  } finally {
    setSendingEmailId(null);
  }
};

  // ---------------- REFRESH ----------------

  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      await Promise.all([
        fetchReferrals(),
        fetchWallet(),
        fetchVoucherRequests(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // ---------------- REFER AGAIN ----------------



  // ---------------- LOADING SCREEN ----------------

  if (loading) {
    return (
      <div className="seller-referral-history-page">
        <Container fluid className="seller-referral-history-container">
          <Card className="seller-referral-history-card">
            <Card.Body>
              <div
                style={{
                  minHeight: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 15,
                }}
              >
                <Spinner animation="border" />
                <p>Loading referral history...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  // ---------------- MAIN UI ----------------

  return (
    <div className="seller-referral-history-page">
      <Container fluid className="seller-referral-history-container">
        <Card className="seller-referral-history-card">
          <Card.Body>
            {/* HEADER */}
            <div className="seller-history-header">
              <div>
                <h1>REFERRAL DASHBOARD</h1>
                <p>
                  Track your referrals, points balance, and redeem rewards.
                </p>
              </div>

              <div className="seller-history-header-buttons">
                <button
                  type="button"
                  className="seller-history-refresh-btn"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <span>⟳</span>
                  )}
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>

            
              </div>
            </div>

            <div className="seller-history-divider" />

            {/* POINTS AND REWARDS */}
            <div className="seller-history-reward-row">
              <div className="seller-history-points-card">
                <div className="seller-history-points-content">
                  <h6>TOTAL POINTS EARNED</h6>

                  <div className="seller-history-points-number">
                    {totalPointsEarned}
                    <span> pts</span>
                  </div>

                  <p>1 Bharee point = ₹1</p>
                
                </div>

                <div className="seller-history-points-icon">🪙</div>
              </div>

              <div className="seller-history-redeem-card">
                <div className="seller-history-redeem-header">
                  <h5>REDEEM REWARDS POINTS</h5>
                  <span>
                    {walletBalance >= REDEMPTION_POINTS
                      ? "Select a redemption option"
                      : "Minimum 1000 points required"}
                  </span>
                </div>

                {/* ALL FOUR OPTIONS */}
                <div className="seller-history-vouchers">
                  {VOUCHER_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`seller-history-voucher ${
                        selectedVoucher === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleVoucherSelect(option.value)}
                      disabled={
                        walletLoading ||
                        walletBalance < REDEMPTION_POINTS ||
                        voucherLoading ||
                        !!existingVoucherRequest
                      }
                    >
                      <div>
                        <small>{option.description}</small>
                        <strong>{option.label}</strong>
                      </div>

                      <div className="seller-voucher-icon">
                        {option.icon}
                      </div>
                    </button>
                  ))}
                </div>

                {selectedVoucher && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      border: "1px solid #c5cae9",
                      borderRadius: 6,
                      background: "#f3f4ff",
                      color: "#283593",
                    }}
                  >
                    Selected option:{" "}
                    <strong>{getVoucherLabel(selectedVoucher)}</strong>
                    <br />
                    Points to redeem:{" "}
                    <strong>{REDEMPTION_POINTS}</strong>
                  </div>
                )}

                {existingVoucherRequest && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      borderRadius: 6,
                      background: "#fff8e1",
                      border: "1px solid #ffe082",
                      color: "#7a5b00",
                    }}
                  >
                    Pending request:{" "}
                    <strong>
                      {getVoucherLabel(existingVoucherRequest)}
                    </strong>
                    <br />
                    Request date:{" "}
                    <strong>
                      {formatDate(
                        getVoucherRequestedAt(existingVoucherRequest)
                      )}
                    </strong>
                  </div>
                )}

                {voucherMessage && (
                  <div
                    role="status"
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      borderRadius: 6,
                      background: "#e8f5e9",
                      border: "1px solid #a5d6a7",
                      color: "#2e7d32",
                    }}
                  >
                    {voucherMessage}
                  </div>
                )}

                {voucherError && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      borderRadius: 6,
                      background: "#ffebee",
                      border: "1px solid #ef9a9a",
                      color: "#c62828",
                    }}
                  >
                    {voucherError}
                  </div>
                )}

                <div className="seller-history-redeem-bottom">
                  <button
                    type="button"
                    className="seller-history-redemption-btn"
                    onClick={handleSubmitRedemption}
                    disabled={
                      voucherLoading ||
                      walletLoading ||
                      walletBalance < REDEMPTION_POINTS ||
                      !selectedVoucher ||
                      !!existingVoucherRequest
                    }
                  >
                    {voucherLoading ? (
                      <>
                        <Spinner animation="border" size="sm" />{" "}
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Redemption <span>→</span>
                      </>
                    )}
                  </button>
                </div>

  

             
              </div>
            </div>

            {/* REFERRAL ERROR */}
            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 20,
                  padding: "12px 15px",
                  borderRadius: 6,
                  background: "#ffecec",
                  color: "#c62828",
                  border: "1px solid #ffcaca",
                }}
              >
                {error}
              </div>
            )}

            {/* SEARCH */}
            <div className="seller-history-search-wrapper">
              <span className="seller-history-search-icon">🔍</span>

              <input
                type="text"
                placeholder="Search by referee, company, category, mobile..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {emailMessage && (
  <p role="status" className="text-success mt-3">
    {emailMessage}
  </p>
)}

{emailError && (
  <p role="alert" className="text-danger mt-3">
    {emailError}
  </p>
)}

            {/* REFERRAL TABLE */}
            <div className="seller-history-table-wrapper">
              <table className="seller-history-table">
                <thead>
                  <tr>
                    <th>SELECT</th>
                    <th>REFER TO</th>
                    <th>MOBILE</th>
                    <th>COMPANY</th>
                    <th>CATEGORY</th>
                    <th>DATE</th>
                    <th>POINT</th>
                    <th>COUPON CODE</th>
                    <th>STATUS</th>
                    <th>ACTION</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredReferrals.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="seller-history-empty">
                        {search
                          ? "No referral history matches your search."
                          : "No referral history found."}
                      </td>
                    </tr>
                  ) : (
                    filteredReferrals.map((item, index) => {
                      const referralPoints = getReferralPoints(item);
                      const status = String(
                        item?.status ?? "Pending"
                      )
                        .trim()
                        .toLowerCase();

                      const statusLabel =
                        status === "active"
                          ? "Active"
                          : status === "registered"
                          ? "Registered"
                          : status === "contacted"
                          ? "Contacted"
                          : status === "rejected"
                          ? "Rejected"
                          : "Pending";

                      const statusClass =
                        statusLabel === "Active"
                          ? "seller-history-status-done"
                          : statusLabel === "Rejected"
                          ? "seller-history-status-rejected"
                          : statusLabel === "Registered" ||
                            statusLabel === "Contacted"
                          ? "seller-history-status-contacted"
                          : "seller-history-status-pending";

                      return (
                        <tr key={item.id ?? item._id ?? index}>
                          <td>
                            <input
                              type="checkbox"
                              className="seller-history-checkbox"
                              aria-label={`Select referral ${getReferralName(item)}`}
                            />
                          </td>

                          <td>
                            <strong>{getReferralName(item)}</strong>
                          </td>

                          <td>{getReferralMobile(item)}</td>
                          <td>{item.company ?? "-"}</td>
                          <td>{item.category ?? "-"}</td>
                          <td>{formatDate(getReferralDate(item))}</td>

                          <td>
                            <span className="seller-history-point-positive">
                              +{referralPoints} pts
                            </span>
                          </td>

                          <td>
                            <span className="seller-history-coupon">
                              {getCouponCode(item) || "--------"}
                            </span>
                          </td>

                          <td>
                            <span className={statusClass}>
                              {statusLabel}
                            </span>
                          </td>

                        <td>
  <div className="seller-history-action-buttons">

    <button
      type="button"
      className="seller-history-mail-btn"
      title="Resend Referral Welcome Email"
      aria-label={`Send welcome email to ${getReferralName(item)}`}
      onClick={() => handleSendReferralEmail(item)}
      disabled={
        sendingEmailId !== null ||
        !(item?.refereeEmail || item?.referee_email)
      }
    >
      {sendingEmailId === (item.id ?? item._id) ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-10 7L2 7" />
        </svg>
      )}
    </button>

    {latestVoucher &&
      isReferralIncludedInVoucher(item) && (
        <button
          type="button"
          className="seller-history-download-btn"
          onClick={() => handleDownloadVoucher(latestVoucher)}
        >
          Download Voucher
        </button>
      )}

  </div>
</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* FOOTER */}
            <div className="seller-history-footer">
              <div className="seller-history-record-count">
                Showing{" "}
                <strong>{filteredReferrals.length > 0 ? 1 : 0}</strong>{" "}
                to <strong>{filteredReferrals.length}</strong> of{" "}
                <strong>{filteredReferrals.length}</strong> records
              </div>

              <div className="seller-history-pagination">
                <button type="button" disabled>
                  Previous
                </button>

                <button
                  type="button"
                  className="active"
                  disabled
                >
                  1
                </button>

                <button type="button" disabled>
                  Next
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ReferralHistory;
